import {
  CommissionReadinessCandidate,
  CommissionReadinessReviewData,
  CommissionReadinessStatus,
  CommissionReadinessSummary,
} from "../../types/revenueOps";

export type CommissionReadinessRow = Record<string, unknown>;

export type CommissionReadinessInput = {
  eligibleRevenueEvents?: CommissionReadinessRow[];
  invoices?: CommissionReadinessRow[];
  payments?: CommissionReadinessRow[];
  adjustments?: CommissionReadinessRow[];
  accounts?: CommissionReadinessRow[];
  attributions?: CommissionReadinessRow[];
  partners?: CommissionReadinessRow[];
  partnerPlanAssignments?: CommissionReadinessRow[];
  commissionPlans?: CommissionReadinessRow[];
  commissionPlanTiers?: CommissionReadinessRow[];
  partnerRelationships?: CommissionReadinessRow[];
  commissionEntries?: CommissionReadinessRow[];
};

const value = (row: CommissionReadinessRow | undefined, key: string): string => {
  const result = row?.[key];
  return result === null || result === undefined ? "" : String(result);
};

const amount = (row: CommissionReadinessRow | undefined, key: string): number => {
  const result = Number(row?.[key] ?? 0);
  return Number.isFinite(result) ? result : 0;
};

const dateOnly = (input: string): string => input ? input.slice(0, 10) : "";

const isOpenDate = (row: CommissionReadinessRow, date: string, startKey: string, endKey: string): boolean => {
  const start = dateOnly(value(row, startKey));
  const end = dateOnly(value(row, endKey));
  return (!start || !date || start <= date) && (!end || !date || end >= date);
};

const eventMetadata = (event: CommissionReadinessRow): CommissionReadinessRow => (event.metadata as CommissionReadinessRow) || {};

export function buildCommissionReadinessReviewKey(eventId: string): string {
  return `commission_readiness:${eventId}`;
}

export function buildCommissionEntrySourceKey(eventId: string): string {
  return `commission_entry:${eventId}:base`;
}

export function canCreateDraftCommissionEntry(candidate: CommissionReadinessCandidate): boolean {
  return candidate.status === "ready"
    && Boolean(candidate.eligible_revenue_event_id && candidate.account_id && candidate.partner_id && candidate.commission_plan_id)
    && candidate.retained_amount > 0
    && Number.isFinite(candidate.draft_rate) && Number(candidate.draft_rate) > 0
    && Number.isFinite(candidate.draft_commission_amount) && Number(candidate.draft_commission_amount) > 0;
}

export function deriveCommissionYear(startDate: string, revenueDate: string): number | undefined {
  if (!startDate || !revenueDate) return undefined;
  const start = new Date(`${dateOnly(startDate)}T00:00:00Z`).getTime();
  const revenue = new Date(`${dateOnly(revenueDate)}T00:00:00Z`).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(revenue) || revenue < start) return undefined;
  return Math.floor((revenue - start) / (365.25 * 24 * 60 * 60 * 1000)) + 1;
}

export function findApplicableCommissionTier(tiers: CommissionReadinessRow[], year: number): CommissionReadinessRow | undefined {
  return tiers.find((tier) => {
    const first = Number(tier.year_start);
    const last = tier.year_end === null || tier.year_end === undefined || tier.year_end === "" ? null : Number(tier.year_end);
    return Number.isFinite(first) && first <= year && (last === null || (Number.isFinite(last) && year <= last));
  });
}

function accountLabel(account: CommissionReadinessRow | undefined, accountId: string): string {
  return value(account, "display_name") || value(account, "business_name") || value(account, "account_name") || value(account, "website_domain") || accountId || "Account mapping needs review";
}

function tierLabel(tier: CommissionReadinessRow | undefined): string | undefined {
  if (!tier) return undefined;
  const start = value(tier, "year_start");
  const end = value(tier, "year_end");
  return end ? `Year ${start}-${end}` : `Year ${start}+`;
}

function hasOpenAdvisoryOverride(relationships: CommissionReadinessRow[], partnerId: string, revenueDate: string): boolean {
  return relationships.some((relationship) => value(relationship, "relationship_type") === "advisory_override" && value(relationship, "child_partner_id") === partnerId && Boolean(relationship.is_active ?? true) && isOpenDate(relationship, revenueDate, "starts_at", "ends_at"));
}

export function deriveCommissionReadinessCandidate(event: CommissionReadinessRow, input: CommissionReadinessInput): CommissionReadinessCandidate {
  const eventId = value(event, "id");
  const sourceKey = value(event, "source_key");
  const invoiceId = value(event, "invoice_id") || value(event, "source_invoice_id");
  const paymentId = value(event, "payment_id") || value(event, "source_payment_id");
  const invoice = (input.invoices || []).find((row) => value(row, "id") === invoiceId);
  const payment = (input.payments || []).find((row) => value(row, "id") === paymentId) || (input.payments || []).find((row) => value(row, "invoice_id") === invoiceId);
  const accountId = value(event, "account_id") || value(invoice, "account_id");
  const account = (input.accounts || []).find((row) => value(row, "account_id") === accountId || value(row, "id") === accountId);
  const revenueDate = dateOnly(value(payment, "received_at") || value(invoice, "paid_at") || value(event, "received_at") || value(event, "staged_at") || value(event, "created_at") || value(invoice, "created_at"));
  const metadata = eventMetadata(event);
  const retainedAmount = Math.max(0, amount(event, "eligible_amount") || amount(metadata, "retained_amount"));
  const reasons: string[] = [];
  const blockerCodes: string[] = [];
  let status: CommissionReadinessStatus = "ready";
  const reviewStatus = value(event, "review_status");
  const retentionStatus = value(event, "retention_status");
  const isManualReviewState = ["pending_review", "staged", "pending", "retained"].includes(reviewStatus) || ["pending_review", "staged", "pending", "retained"].includes(retentionStatus);
  if (!isManualReviewState) {
    status = "review_needed";
    blockerCodes.push("event_not_pending_review");
    reasons.push("The eligible revenue event is not in a recognized manual review state.");
  }

  const sameSource = sourceKey ? (input.eligibleRevenueEvents || []).filter((row) => value(row, "source_key") === sourceKey) : [];
  const existingCommissionEntry = (input.commissionEntries || []).find((entry) => value(entry, "revenue_event_id") === eventId || (sourceKey && value(entry, "source_key") === sourceKey));
  const existingDraft = Boolean(existingCommissionEntry && (value(existingCommissionEntry, "source_status") === "pending" || value(existingCommissionEntry, "source_status") === "pending_review" || value(existingCommissionEntry, "review_status") === "pending_review"));
  const alreadyCommissioned = Boolean(existingCommissionEntry);
  if (alreadyCommissioned) {
    status = existingDraft ? "draft_created" : "already_commissioned";
    reasons.push(existingDraft ? "A pending commission draft already references this eligible revenue event." : "A commission entry already references this eligible revenue event.");
  } else if (retainedAmount <= 0) {
    status = "excluded";
    reasons.push("The staged event has no retained revenue after refunds or credits.");
    blockerCodes.push("zero_retained_amount");
  } else if (!accountId) {
    status = "blocked";
    blockerCodes.push("missing_account_mapping");
    reasons.push("The eligible revenue event is not linked to a Toren account.");
  } else if (!invoice) {
    status = "blocked";
    blockerCodes.push("missing_invoice");
    reasons.push("The source invoice could not be resolved from the billing mirror.");
  } else if (!payment) {
    status = "blocked";
    blockerCodes.push("missing_payment");
    reasons.push("The staged event is missing a linked payment mirror row.");
  }

  if (accountId && !account) {
    status = status === "ready" ? "review_needed" : status;
    blockerCodes.push("account_mapping_needs_review");
    reasons.push("The account ID does not resolve to the core account directory.");
  }
  if (payment && ["failed", "void", "canceled"].includes(value(payment, "status"))) {
    status = "excluded";
    blockerCodes.push("payment_not_collected");
    reasons.push("The payment mirror is not a collected payment.");
  }
  if (invoice && ["void", "uncollectible", "open", "draft"].includes(value(invoice, "status"))) {
    status = "excluded";
    blockerCodes.push("invoice_not_paid");
    reasons.push("The source invoice is not in a paid state.");
  }

  const eventAttributionId = value(event, "attribution_id") || value(metadata, "attribution_id");
  const attributions = (input.attributions || []).filter((row) => {
    const matchesExplicitAttribution = eventAttributionId && value(row, "id") === eventAttributionId;
    const matchesAccount = !eventAttributionId && value(row, "account_id") === accountId;
    return (matchesExplicitAttribution || matchesAccount) && ["active", "approved"].includes(value(row, "status")) && isOpenDate(row, revenueDate, "effective_start", "effective_end");
  });
  const attribution = attributions.sort((a, b) => value(b, "commission_start_date").localeCompare(value(a, "commission_start_date")))[0];
  const partnerId = value(attribution, "partner_id");
  const partner = (input.partners || []).find((row) => value(row, "id") === partnerId);
  if (!attribution && status === "ready") {
    status = "blocked";
    blockerCodes.push("missing_attribution");
    reasons.push("No active attribution was found for the revenue date.");
  }
  if (attribution && !partner && status === "ready") {
    status = "blocked";
    blockerCodes.push("missing_partner");
    reasons.push("The attributed partner could not be resolved.");
  } else if (partner && value(partner, "status") !== "active" && status === "ready") {
    status = "blocked";
    blockerCodes.push("inactive_partner");
    reasons.push("The attributed partner is not active.");
  }

  const assignment = partnerId && (input.partnerPlanAssignments || []).filter((row) => value(row, "partner_id") === partnerId && isOpenDate(row, revenueDate, "starts_at", "ends_at")).sort((a, b) => value(b, "starts_at").localeCompare(value(a, "starts_at")))[0];
  const planId = value(assignment, "plan_id");
  const plan = (input.commissionPlans || []).find((row) => value(row, "id") === planId);
  if (partner && !assignment && status === "ready") {
    status = "blocked";
    blockerCodes.push("missing_active_plan_assignment");
    reasons.push("The partner has no active commission plan assignment for the revenue date.");
  }
  if (assignment && !plan && status === "ready") {
    status = "blocked";
    blockerCodes.push("missing_commission_plan");
    reasons.push("The assigned commission plan could not be resolved.");
  }

  const tiers = (input.commissionPlanTiers || []).filter((tier) => value(tier, "plan_id") === planId);
  const commissionStart = value(attribution, "commission_start_date");
  const year = deriveCommissionYear(commissionStart, revenueDate);
  const tier = year ? findApplicableCommissionTier(tiers, year) : undefined;
  if (plan && !year && status === "ready") {
    status = "review_needed";
    blockerCodes.push("commission_start_date_uncertain");
    reasons.push("The commission start date is not available, so the applicable plan year cannot be determined safely.");
  } else if (plan && !tier && status === "ready") {
    status = "blocked";
    blockerCodes.push("missing_applicable_tier");
    reasons.push("The commission plan has no applicable tier for the revenue date.");
  }

  if (sameSource.length > 1 && status === "ready") {
    status = "review_needed";
    blockerCodes.push("duplicate_source_key");
    reasons.push("Multiple staged events share the same source key.");
  }
  const advisoryOverrideReview = Boolean(partnerId && hasOpenAdvisoryOverride(input.partnerRelationships || [], partnerId, revenueDate));
  if (advisoryOverrideReview) reasons.push("Potential override review: an advisory relationship may apply to this partner's commission.");

  const draftRate = tier ? Number(tier.commission_rate) : undefined;
  const draftCommissionAmount = draftRate !== undefined && Number.isFinite(draftRate) && status === "ready" ? Number((retainedAmount * draftRate).toFixed(2)) : undefined;
  return {
    eligible_revenue_event_id: eventId,
    source_key: sourceKey,
    account_id: accountId || undefined,
    account_label: accountLabel(account, accountId),
    invoice_id: invoiceId || undefined,
    invoice_number: value(invoice, "invoice_number") || undefined,
    payment_id: paymentId || value(payment, "id") || undefined,
    retained_amount: retainedAmount,
    currency: value(event, "currency") || value(invoice, "currency") || "usd",
    partner_id: partnerId || undefined,
    partner_label: value(partner, "display_name") || value(partner, "legal_name") || undefined,
    commission_plan_id: planId || undefined,
    commission_plan_name: value(plan, "plan_name") || undefined,
    applicable_tier_label: tierLabel(tier),
    draft_rate: draftRate,
    draft_commission_amount: draftCommissionAmount,
    status,
    existing_commission_status: value(existingCommissionEntry, "status") || undefined,
    existing_commission_review_status: value(existingCommissionEntry, "review_status") || undefined,
    blocker_codes: blockerCodes,
    reasons,
    revenue_date: revenueDate || undefined,
    advisory_override_review: advisoryOverrideReview,
    review_key: buildCommissionReadinessReviewKey(eventId),
  };
}

export function summarizeCommissionReadiness(candidates: CommissionReadinessCandidate[]): CommissionReadinessSummary {
  return {
    staged_events: candidates.length,
    staged_event_records: candidates.length,
    ready: candidates.filter((row) => row.status === "ready").length,
    blocked: candidates.filter((row) => row.status === "blocked").length,
    review_needed: candidates.filter((row) => row.status === "review_needed").length,
    already_commissioned: candidates.filter((row) => row.status === "already_commissioned").length,
    draft_created: candidates.filter((row) => row.status === "draft_created").length,
    excluded: candidates.filter((row) => row.status === "excluded").length,
  };
}

export function deriveCommissionReadinessReviewData(input: CommissionReadinessInput): CommissionReadinessReviewData {
  const events = input.eligibleRevenueEvents || [];
  const candidates = events.map((event) => deriveCommissionReadinessCandidate(event, input));
  return { candidates, summary: summarizeCommissionReadiness(candidates) };
}
